/**
 * Procedural wheel artwork.
 *
 * The rotating face, stationary light pass, and stationary hub are separate
 * textures. That separation is the important visual change: highlights now
 * remain attached to the studio lights while the painted wheel turns below.
 */
import { Texture } from 'pixi.js';

import { WEDGE_COUNT, WEDGE_DEG, WEDGES } from '../palette';
import type { Wedge } from '../types';
import { WHEEL_VISUAL as THEME } from './wheelVisual';

const FACE_OUTER = 0.878;
const RIM_INNER = 0.9;
const RIM_OUTER = 0.968;

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const value = Number.parseInt(clean, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mix(a: string, b: string, amount: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const m = Math.max(0, Math.min(1, amount));
  return `rgb(${Math.round(ca.r + (cb.r - ca.r) * m)}, ${Math.round(ca.g + (cb.g - ca.g) * m)}, ${Math.round(ca.b + (cb.b - ca.b) * m)})`;
}

function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function paintFor(wedge: Wedge): string {
  if (wedge.kind === 'bankrupt') return THEME.bankruptColor;
  if (wedge.kind === 'lose_turn') return THEME.loseTurnColor;
  if (wedge.kind === 'free_play') return THEME.freePlayColor;
  if (wedge.value >= 5000) return THEME.topDollarColor;
  return THEME.cashColors[wedge.index % THEME.cashColors.length]!;
}

function inkFor(wedge: Wedge, fill: string): string {
  if (wedge.kind === 'bankrupt') return THEME.lightInk;
  return luminance(fill) > 0.57 ? THEME.darkInk : THEME.lightInk;
}

function wedgeAngles(index: number): { start: number; end: number; mid: number } {
  const start = ((-90 + (index - 0.5) * WEDGE_DEG) * Math.PI) / 180;
  const end = start + (WEDGE_DEG * Math.PI) / 180;
  return { start, end, mid: (start + end) / 2 };
}

function drawShape(ctx: CanvasRenderingContext2D, shape: Wedge['shape'], size: number): void {
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(0, 0, size, 0, Math.PI * 2);
      break;
    case 'triangle':
      ctx.moveTo(0, -size);
      ctx.lineTo(size * 0.92, size * 0.68);
      ctx.lineTo(-size * 0.92, size * 0.68);
      ctx.closePath();
      break;
    case 'square':
      ctx.rect(-size * 0.78, -size * 0.78, size * 1.56, size * 1.56);
      break;
    case 'diamond':
      ctx.moveTo(0, -size);
      ctx.lineTo(size, 0);
      ctx.lineTo(0, size);
      ctx.lineTo(-size, 0);
      ctx.closePath();
      break;
    case 'star':
      for (let i = 0; i < 10; i += 1) {
        const pointRadius = i % 2 === 0 ? size : size * 0.45;
        const angle = (i * Math.PI) / 5 - Math.PI / 2;
        const x = Math.cos(angle) * pointRadius;
        const y = Math.sin(angle) * pointRadius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    case 'dot':
      ctx.arc(0, 0, size * 0.6, 0, Math.PI * 2);
      break;
  }
  ctx.fill();
}

function drawRadialLabel(
  ctx: CanvasRenderingContext2D,
  wedge: Wedge,
  radius: number,
  mid: number,
  fill: string,
): void {
  ctx.save();
  ctx.rotate(mid + Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  const ink = inkFor(wedge, fill);
  const outline = luminance(ink) > 0.57 ? 'rgba(5, 8, 15, 0.82)' : 'rgba(255, 255, 255, 0.84)';
  ctx.fillStyle = ink;
  ctx.strokeStyle = outline;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.28)';
  ctx.shadowBlur = radius * 0.004;
  ctx.shadowOffsetY = radius * 0.003;

  if (wedge.kind === 'cash') {
    const digits = String(wedge.value).split('');
    const fontSize = radius * (digits.length >= 4 ? 0.063 : 0.071);
    const lineHeight = fontSize * 1.02;
    const centerY = -radius * 0.655;
    const startY = centerY - ((digits.length - 1) * lineHeight) / 2;
    ctx.font = `950 ${fontSize}px Inter, Arial, sans-serif`;
    ctx.lineWidth = Math.max(3, fontSize * 0.14);
    digits.forEach((digit, index) => {
      const y = startY + index * lineHeight;
      ctx.strokeText(digit, 0, y);
      ctx.fillText(digit, 0, y);
    });
    ctx.font = `950 ${fontSize * 0.48}px Inter, Arial, sans-serif`;
    ctx.lineWidth = Math.max(2, fontSize * 0.1);
    ctx.strokeText('$', -fontSize * 0.43, startY);
    ctx.fillText('$', -fontSize * 0.43, startY);
  } else {
    const word =
      wedge.kind === 'bankrupt' ? 'BANKRUPT' : wedge.kind === 'lose_turn' ? 'LOSETURN' : 'FREEPLAY';
    const letters = word.split('');
    const fontSize = radius * (letters.length > 8 ? 0.031 : 0.035);
    const lineHeight = fontSize * 1.18;
    const centerY = -radius * 0.65;
    const startY = centerY - ((letters.length - 1) * lineHeight) / 2;
    ctx.font = `950 ${fontSize}px Inter, Arial, sans-serif`;
    ctx.lineWidth = Math.max(2, fontSize * 0.13);
    letters.forEach((letter, index) => {
      const y = startY + index * lineHeight;
      ctx.strokeText(letter, 0, y);
      ctx.fillText(letter, 0, y);
    });
  }

  if (THEME.showShapes) {
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = ink;
    ctx.translate(0, -radius * 0.41);
    drawShape(ctx, wedge.shape, radius * 0.026);
  }
  ctx.restore();
}

function drawWedge(ctx: CanvasRenderingContext2D, radius: number, wedge: Wedge): void {
  const { start, end, mid } = wedgeAngles(wedge.index);
  const fill = paintFor(wedge);
  const faceRadius = radius * FACE_OUTER;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, faceRadius, start, end);
  ctx.closePath();
  ctx.clip();

  if (THEME.gradientStrength > 0.2) {
    const gradient = ctx.createRadialGradient(
      Math.cos(mid) * radius * 0.12,
      Math.sin(mid) * radius * 0.12,
      radius * 0.04,
      Math.cos(mid) * radius * 0.76,
      Math.sin(mid) * radius * 0.76,
      radius * 0.62,
    );
    gradient.addColorStop(0, mix(fill, '#FFFFFF', THEME.gradientStrength));
    gradient.addColorStop(0.58, fill);
    gradient.addColorStop(1, mix(fill, '#02050A', THEME.gradientStrength * 0.52));
    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = fill;
  }
  ctx.fillRect(-radius, -radius, radius * 2, radius * 2);

  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = `rgba(255, 255, 255, ${THEME.gradientStrength * 0.22})`;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, faceRadius, start, mid);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = THEME.dividerColor;
  ctx.lineWidth = Math.max(2, radius * 0.005);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(Math.cos(start) * faceRadius, Math.sin(start) * faceRadius);
  ctx.stroke();

  drawRadialLabel(ctx, wedge, radius, mid, fill);
}

function drawFaceRings(ctx: CanvasRenderingContext2D, radius: number): void {
  ctx.strokeStyle = THEME.faceRingColor;
  ctx.lineWidth = radius * 0.004;
  for (const ring of [0.36, 0.55, 0.73]) {
    ctx.beginPath();
    ctx.arc(0, 0, radius * ring, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawRim(ctx: CanvasRenderingContext2D, radius: number): void {
  const outer = radius * RIM_OUTER;
  const inner = radius * RIM_INNER;
  const gradient = ctx.createRadialGradient(
    -radius * 0.22,
    -radius * 0.28,
    radius * 0.1,
    0,
    0,
    outer,
  );
  const [bright, high, mid, dark] = THEME.rimStops;
  gradient.addColorStop(0, bright);
  gradient.addColorStop(0.28, high);
  gradient.addColorStop(0.67, mid);
  gradient.addColorStop(1, dark);

  ctx.beginPath();
  ctx.arc(0, 0, outer, 0, Math.PI * 2);
  ctx.arc(0, 0, inner, 0, Math.PI * 2, true);
  ctx.fillStyle = gradient;
  ctx.fill('evenodd');

  ctx.strokeStyle = THEME.rimEdge;
  ctx.lineWidth = radius * 0.015;
  ctx.beginPath();
  ctx.arc(0, 0, outer - radius * 0.008, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = THEME.rimHighlight;
  ctx.globalAlpha = 0.72;
  ctx.lineWidth = radius * 0.005;
  ctx.beginPath();
  ctx.arc(0, 0, inner + radius * 0.009, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;

  const pegRadius = radius * 0.0135;
  for (let i = 0; i < WEDGE_COUNT; i += 1) {
    const angle = ((-90 + (i - 0.5) * WEDGE_DEG) * Math.PI) / 180;
    const x = Math.cos(angle) * radius * 0.938;
    const y = Math.sin(angle) * radius * 0.938;
    const peg = ctx.createRadialGradient(
      x - pegRadius * 0.36,
      y - pegRadius * 0.4,
      pegRadius * 0.05,
      x,
      y,
      pegRadius,
    );
    peg.addColorStop(0, THEME.rimHighlight);
    peg.addColorStop(0.52, THEME.rimStops[1]);
    peg.addColorStop(1, THEME.rimStops[3]);
    ctx.fillStyle = peg;
    ctx.beginPath();
    ctx.arc(x, y, pegRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

function makeCanvas(
  size: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  return ctx ? { canvas, ctx } : null;
}

function drawWheelFace(ctx: CanvasRenderingContext2D, size: number): void {
  const radius = size * 0.48;
  ctx.translate(size / 2, size / 2);
  for (const wedge of WEDGES) drawWedge(ctx, radius, wedge);
  drawFaceRings(ctx, radius);
  drawRim(ctx, radius);
}

function drawPreviewHub(ctx: CanvasRenderingContext2D, size: number): void {
  const center = size / 2;
  const radius = size * 0.085;
  const [bright, mid, dark] = THEME.hubStops;
  const hub = ctx.createRadialGradient(
    center - radius * 0.3,
    center - radius * 0.34,
    radius * 0.08,
    center,
    center,
    radius,
  );
  hub.addColorStop(0, bright);
  hub.addColorStop(0.56, mid);
  hub.addColorStop(1, dark);
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = radius * 0.34;
  ctx.shadowOffsetY = radius * 0.18;
  ctx.fillStyle = hub;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = THEME.hubAccent;
  ctx.lineWidth = radius * 0.13;
  ctx.beginPath();
  ctx.arc(center, center, radius * 0.84, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = THEME.hubMark;
  ctx.font = `950 ${radius * 0.72}px Fraunces, Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('W', center, center + radius * 0.04);
  ctx.restore();
}

/** Paints the same wheel face used by Pixi into a DOM canvas for static previews. */
export function paintWheelPreview(canvas: HTMLCanvasElement, size = 1200): void {
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);
  ctx.save();
  drawWheelFace(ctx, size);
  ctx.restore();
  drawPreviewHub(ctx, size);
}

export function createWheelTexture(size = 1600): Texture | null {
  const target = makeCanvas(size);
  if (!target) return null;
  const { canvas, ctx } = target;
  drawWheelFace(ctx, size);
  return Texture.from(canvas);
}

export function createWheelLightingTexture(size = 1600): Texture | null {
  const target = makeCanvas(size);
  if (!target) return null;
  const { canvas, ctx } = target;
  const center = size / 2;
  const radius = size * 0.465;

  ctx.save();
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.clip();

  const sweep = ctx.createLinearGradient(size * 0.16, size * 0.14, size * 0.8, size * 0.86);
  sweep.addColorStop(0, 'rgba(255, 255, 255, 0)');
  sweep.addColorStop(0.37, THEME.lightTint);
  sweep.addColorStop(0.5, 'rgba(255, 255, 255, 0.08)');
  sweep.addColorStop(0.63, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = sweep;
  ctx.fillRect(0, 0, size, size);

  const shade = ctx.createRadialGradient(
    size * 0.37,
    size * 0.3,
    size * 0.08,
    center,
    center,
    radius,
  );
  shade.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
  shade.addColorStop(0.62, 'rgba(255, 255, 255, 0)');
  shade.addColorStop(1, 'rgba(0, 5, 15, 0.28)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();

  ctx.strokeStyle = THEME.rimHighlight;
  ctx.globalAlpha = 0.76;
  ctx.lineWidth = size * 0.008;
  ctx.beginPath();
  ctx.arc(center, center, radius * 0.99, Math.PI * 1.08, Math.PI * 1.63);
  ctx.stroke();
  return Texture.from(canvas);
}

export function createWheelHubTexture(size = 1600): Texture | null {
  const target = makeCanvas(size);
  if (!target) return null;
  const { canvas, ctx } = target;
  const center = size / 2;
  const radius = size * 0.085;
  const [bright, mid, dark] = THEME.hubStops;

  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = radius * 0.34;
  ctx.shadowOffsetY = radius * 0.18;
  const outer = ctx.createRadialGradient(
    center - radius * 0.3,
    center - radius * 0.34,
    radius * 0.08,
    center,
    center,
    radius,
  );
  outer.addColorStop(0, bright);
  outer.addColorStop(0.56, mid);
  outer.addColorStop(1, dark);
  ctx.fillStyle = outer;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';

  ctx.strokeStyle = THEME.hubAccent;
  ctx.lineWidth = radius * 0.13;
  ctx.beginPath();
  ctx.arc(center, center, radius * 0.84, 0, Math.PI * 2);
  ctx.stroke();

  const capRadius = radius * 0.56;
  const cap = ctx.createRadialGradient(
    center - capRadius * 0.3,
    center - capRadius * 0.36,
    capRadius * 0.04,
    center,
    center,
    capRadius,
  );
  cap.addColorStop(0, THEME.hubAccent);
  cap.addColorStop(1, mid);
  ctx.fillStyle = cap;
  ctx.beginPath();
  ctx.arc(center, center, capRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = THEME.hubMark;
  ctx.font = `950 ${radius * 0.72}px Fraunces, Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('W', center, center + radius * 0.04);
  return Texture.from(canvas);
}
